import Vue from "vue"
import { MutationTree, ActionTree, GetterTree } from "vuex"

import { RootState } from "~/store"
import Bucket from "~/types/bucket"
import Influencer from "~/types/influencer"

export const state = () => ({
  buckets: [] as Bucket[],
  inspectedInfluencer: null as null | Influencer,
})
export type UserFavoriteState = ReturnType<typeof state>

export const mutations: MutationTree<UserFavoriteState> = {
  SET_BUCKETS(state, bucketArray: Bucket[]) {
    Vue.set(state, "buckets", [...bucketArray])
  },
  ADD_BUCKETS(state, bucketArray: Bucket[]) {
    Vue.set(state, "buckets", [...state.buckets, ...bucketArray])
  },
  ADD_BUCKET(state, bucket) {
    state.buckets.push(bucket)
  },
  REMOVE_BUCKET(state, bucketId) {
    state.buckets.splice(
      state.buckets.findIndex((elem) => {
        return elem.id === bucketId
      }),
      1
    )
  },
  REMOVE_BUCKETS(state) {
    Vue.set(state, "buckets", [])
  },
  UPDATE_BUCKET(state, bucket) {
    for (let i = state.buckets.length - 1; i >= 0; i--) {
      const elem = state.buckets[i]

      if (elem.id === bucket.id) {
        Vue.set(state.buckets, i, { ...bucket })
        break
      }
    }
  },
  ADD_INF_FROM_BUCKET(
    state,
    { bucketId, influencerId }: { bucketId: number; influencerId: number }
  ) {
    for (let i = state.buckets.length - 1; i >= 0; i--) {
      const elem = state.buckets[i]

      if (elem.id === bucketId) elem.influencers.push(influencerId)
    }
  },
  REMOVE_INF_FROM_BUCKET(
    state,
    { bucketId, influencerId }: { bucketId: number; influencerId: number }
  ) {
    for (let i = state.buckets.length - 1; i >= 0; i--) {
      const elem = state.buckets[i]

      if (elem.id === bucketId)
        elem.influencers.splice(elem.influencers.indexOf(influencerId), 1)
    }
  },
  RESET(state) {
    while (state.buckets.length) state.buckets.splice(0, 1)
    state.inspectedInfluencer = null
  },
  SET_INSPECTED_INFLUENCER(state, influencer: Influencer | null) {
    Vue.set(state, "inspectedInfluencer", influencer)
  },
}

export const actions: ActionTree<UserFavoriteState, RootState> = {
  FETCH_BUCKETS: async function ({ commit, getters }): Promise<void> {
    return new Promise((resolve) => {
      this.$axios
        .$get("/agency/favorites/preview/")
        .then((data: Bucket[]) => {
          commit(
            "ADD_BUCKETS",
            data.filter((elem) => {
              return !getters.IDS.includes(elem.id)
            })
          )
          resolve()
        })
        .catch(() => {
          resolve()
        })
    })
  },
  FETCH_INFLUENCERS({ dispatch, state }): Promise<void> {
    return new Promise(async (resolve, reject) => {
      dispatch(
        "influencers/FETCH_SET",
        [
          ...new Set(
            state.buckets.reduce((accumulator, bucket) => {
              return accumulator.concat(bucket.influencers)
            }, [] as number[])
          ),
        ],
        { root: true }
      )
        .then(() => {
          resolve()
        })
        .catch((err) => {
          console.log("Failed to fetch buckets", err)
          reject(err)
        })
    })
  },
  async FETCH_BUCKET({ commit, getters }, bucketId: number) {
    const bucket = await this.$axios.$get(`agency/favorites/${bucketId}/`)

    if (!getters.IDS.includes(bucket.id)) commit("ADD_BUCKET", bucket)
    else commit("UPDATE_BUCKET", bucket)
  },
  async DELETE_BUCKET({ commit }, bucket: Bucket) {
    await this.$axios.$delete("/agency/favorites/" + bucket.id + "/")

    commit("REMOVE_BUCKET", bucket.id)
  },
  async PATCH_BUCKET({ commit }, bucket: Bucket) {
    const resp = await this.$axios.$patch(
      "/agency/favorites/" + bucket.id + "/",
      bucket
    )

    commit("UPDATE_BUCKET", resp)
  },
}

export const getters: GetterTree<UserFavoriteState, RootState> = {
  GET_BUCKET_FROM_ID(state) {
    return function (id: number) {
      return state.buckets.find((elem) => {
        return elem.id === id
      })
    }
  },
  BUCKET_EXISTS(_state, getters) {
    return function (id: number) {
      return getters.IDS.indexOf(id) !== -1
    }
  },
  IDS(state) {
    const ret = []

    for (let i = state.buckets.length - 1; i >= 0; i--)
      ret.push(state.buckets[i].id)
    return ret
  },
  INF_ID_IS_IN_BUCKET(state) {
    return function (id: number) {
      return state.buckets.some((bucket) => {
        return bucket.influencers.includes(id)
      })
    }
  },
}
